import type { ClientMsg, ServerMsg } from "./types";

/**
 * The protocol version this client speaks: the server's proto.Version, sent
 * with every login and compared with the one the welcome carries. A Go test
 * (TestTheBrowserSpeaksTheServersVersion) keeps the two the same number.
 * See docs/protocol.md for what moves it.
 */
export const PROTOCOL_VERSION = 2;

export type Handler = (msg: ServerMsg) => void;

/**
 * Thin WebSocket wrapper with JSON framing.
 *
 * The one piece of bookkeeping here beyond the framing is which socket is
 * the current one. A browser's WebSocket goes on firing its handlers after
 * anybody has stopped caring about it — an abandoned connection attempt
 * still reports its failure, and a socket whose close was already acted on
 * still delivers that close a moment later — and every one of those events
 * used to be taken as the live connection's. That was harmless while there
 * was only ever one socket in a page's life, and it stopped being harmless
 * the moment a dropped connection started opening a second one: the first
 * socket's late close would be read as the second one's, and the player
 * would be sent to the login screen while a perfectly good connection was
 * open behind it.
 *
 * So `ws` is set the moment a socket is made rather than when it opens,
 * and every handler asks whether it is still the socket this Net speaks
 * through before doing anything at all. Sockets we have let go of are
 * silent, which is what letting go of them was supposed to mean.
 */
export class Net {
  /** The socket being opened, or the open one; null when there is none. */
  private ws: WebSocket | null = null;
  /** The pending connect's reject, so that giving up settles its promise. */
  private fail: ((err: Error) => void) | null = null;
  /**
   * When the socket last delivered anything at all, by performance.now(),
   * or when it opened if it has said nothing yet. It is what tells a socket
   * that is open from one that only says it is: see drop.
   */
  private heardAt = 0;
  onmessage: Handler = () => {};
  onclose: (reason: string) => void = () => {};

  connect(): Promise<void> {
    const proto = location.protocol === "https:" ? "wss:" : "ws:";
    const url = `${proto}//${location.host}/ws`;
    // Whatever we were talking to, we are not any more. Without this a
    // second connect would leave the first socket open and unwatched,
    // holding a session on the server that nobody is reading.
    this.close();
    return new Promise((resolve, reject) => {
      const ws = new WebSocket(url);
      this.ws = ws;
      this.fail = reject;
      ws.onopen = () => {
        if (this.ws !== ws) return;
        this.heardAt = performance.now();
        this.fail = null;
        resolve();
      };
      // A connection that was never made is not a connection that was
      // lost, and keeping the two apart is what the `fail` bookkeeping is
      // for: a refused attempt settles the promise its caller is waiting
      // on and says nothing else. It used to do both — reject here and
      // report a disconnect from onclose a moment later — which was merely
      // untidy while the only caller was the login button, and is a retry
      // loop that schedules itself twice over now that a dropped
      // connection tries again on a timer.
      //
      // Only a connection still being made, though. A socket that was open
      // gets an error too when it fails — a laptop waking with its network
      // gone (net::ERR_NETWORK_IO_SUSPENDED, ERR_NETWORK_CHANGED), a frame
      // the browser will not read — and the close follows it. Letting go of
      // that socket here made the close that follows somebody else's, so
      // it was never reported: no reconnect, no banner, the ping at a dash
      // and the world frozen until the page was reloaded (2026-10-15: "If I
      // abandon the browser for a while and come back the game freezes and
      // doesn't respond. It doesn't seem to be detecting disconnect"). The
      // close is what reports a lost connection, so it is left to do it.
      ws.onerror = () => {
        if (this.ws !== ws || !this.fail) return;
        this.fail = null;
        this.ws = null;
        reject(new Error("Could not connect to the game server."));
      };
      ws.onmessage = (ev) => {
        if (this.ws !== ws) return;
        this.heardAt = performance.now(); // anything at all is proof of life, parseable or not
        let msg: ServerMsg;
        try {
          msg = JSON.parse(ev.data);
        } catch {
          return;
        }
        this.onmessage(msg);
      };
      ws.onclose = (ev) => {
        if (this.ws !== ws) return;
        this.ws = null;
        const fail = this.fail;
        this.fail = null;
        if (fail) {
          fail(new Error("Could not connect to the game server."));
          return;
        }
        this.onclose(ev.reason || "Connection closed.");
      };
    });
  }

  /**
   * Let go of the socket without hearing any more from it: no onclose, no
   * late message, no failure from a connect that was still in flight.
   *
   * This is the other half of the identity check above, and it is what the
   * reconnect gives up with. A socket that is still opening when the last
   * of the three seconds runs out would otherwise open a moment later, be
   * greeted, and draw a world behind the login screen the player is already
   * typing into. The pending promise is rejected rather than dropped,
   * because an awaited promise that never settles is a reconnect attempt
   * that never finishes and never can.
   */
  close(): void {
    const ws = this.ws;
    const fail = this.fail;
    this.ws = null;
    this.fail = null;
    if (fail) fail(new Error("The connection attempt was given up on."));
    if (!ws) return;
    ws.onopen = null;
    ws.onerror = null;
    ws.onmessage = null;
    ws.onclose = null;
    ws.close();
  }

  /**
   * Give up on a socket that is open on paper and dead in fact, and report it
   * exactly as a close is reported, so that whatever a lost connection sets
   * going — the reconnect — is set going by this too.
   *
   * A socket can stay OPEN for many minutes after the other end is gone. A
   * laptop that slept, a phone that put its browser away, a mobile network
   * that forgot the route: the server gives up on the connection and closes
   * its end, the close never arrives, and the browser goes on believing in a
   * connection that nothing will ever come down again. Nothing it sends fails
   * — it is simply buffered for a peer that is not there — and TCP takes a
   * long time to call that a failure. The world stops moving, the ping goes
   * to a dash, and no close event comes to start the reconnect, which is the
   * freeze this is for (2026-09-22: "When I leave the tab idle for a period,
   * when I come back the game has frozen... Ping reads -"). Deciding that the
   * socket is dead is the caller's business, since it is the caller who knows
   * what it asked and has not been answered; this is what it does about it.
   */
  drop(reason: string): void {
    if (!this.connected && !this.lost) return;
    this.close();
    this.onclose(reason);
  }

  /** When the socket last delivered anything, by performance.now(). */
  get heard(): number {
    return this.heardAt;
  }

  /**
   * A socket that opened and has since closed, or begun to, without its
   * close reaching onclose. Nothing should ever leave one behind, since the
   * close event is what onclose is for; this is the watchdog's way of making
   * sure that a socket the browser has given up on cannot go unreported
   * whatever the browser did or did not tell us about it. See drop.
   */
  get lost(): boolean {
    return this.ws !== null && this.fail === null && this.ws.readyState >= WebSocket.CLOSING;
  }

  get connected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  send(msg: ClientMsg): void {
    if (this.connected) this.ws!.send(JSON.stringify(msg));
  }
}
